/** @format — WebSocket live voice session (PCM in/out) */

import { API_BASE_URL } from "../../config";
import { getToken } from "../../utils/tokenStorage";

const LIVE_PATH = "/v1/agents/live";
const INPUT_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;
const CAPTURE_BATCH_MS = 50;
const CONNECT_TIMEOUT_MS = 12_000;
const READY_TIMEOUT_MS = 15_000;
const MIC_TIMEOUT_MS = 12_000;
const STOP_DRAIN_MS = 1_000;

function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

function describeCloseEvent(event, { established = false } = {}) {
  const code = event?.code;
  const reason = event?.reason?.trim();
  if (code === 1008 && reason) return reason;
  if (established) {
    const detail = reason || `code ${code || "unknown"}`;
    return `The voice service ended the session (${detail}). Anything said since the last reply was not saved.`;
  }
  if (code === 1006) {
    return "Could not reach the voice service. The live voice endpoint may not be deployed yet.";
  }
  if (reason) return reason;
  if (code) return `Voice connection closed (code ${code}).`;
  return "Voice connection closed unexpectedly.";
}

function waitForSocketClose(socket, timeoutMs) {
  if (!socket || socket.readyState === WebSocket.CLOSED) return Promise.resolve();

  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    socket.addEventListener(
      "close",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

const CAPTURE_WORKLET = `
class PcmCapture extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length) this.port.postMessage(new Float32Array(channel));
    return true;
  }
}
registerProcessor("pcm-capture", PcmCapture);
`;

function getVoiceBaseUrl() {
  return (API_BASE_URL || window.location.origin).replace(/\/$/, "");
}

function liveSocketUrl({ baseUrl, token, threadId }) {
  const httpBase = baseUrl.replace(/\/$/, "");
  const wsBase = httpBase.replace(/^http/i, (match) => (match.toLowerCase() === "https" ? "wss" : "ws"));
  const url = new URL(`${wsBase}${LIVE_PATH}`);
  const rawToken = token?.trim();
  if (rawToken && !["undefined", "null", "none", ""].includes(rawToken.toLowerCase())) {
    url.searchParams.set("token", rawToken.replace(/^Bearer\s+/i, ""));
  }
  if (threadId) url.searchParams.set("threadId", threadId);
  return url.toString();
}

function resample(samples, fromRate, toRate) {
  if (fromRate === toRate) return samples;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(samples.length / ratio));
  for (let i = 0; i < out.length; i += 1) {
    const pos = i * ratio;
    const left = Math.floor(pos);
    const right = Math.min(left + 1, samples.length - 1);
    const weight = pos - left;
    out[i] = samples[left] * (1 - weight) + samples[right] * weight;
  }
  return out;
}

function floatToPcm16(samples) {
  const buffer = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
  }
  return buffer;
}

function base64ToFloat32(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const view = new DataView(bytes.buffer);
  const out = new Float32Array(bytes.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = view.getInt16(i * 2, true) / 0x8000;
  return out;
}

class PcmBatcher {
  constructor(sampleRate, onBatch) {
    this.batchSize = Math.max(1, Math.round((sampleRate * CAPTURE_BATCH_MS) / 1000));
    this.onBatch = onBatch;
    this.queue = [];
    this.queuedSamples = 0;
    this.headOffset = 0;
  }

  push(samples) {
    if (!samples?.length) return;
    this.queue.push(samples);
    this.queuedSamples += samples.length;

    while (this.queuedSamples >= this.batchSize) {
      const batch = new Float32Array(this.batchSize);
      let written = 0;
      while (written < batch.length) {
        const head = this.queue[0];
        const available = head.length - this.headOffset;
        const count = Math.min(available, batch.length - written);
        batch.set(head.subarray(this.headOffset, this.headOffset + count), written);
        written += count;
        this.headOffset += count;
        this.queuedSamples -= count;
        if (this.headOffset === head.length) {
          this.queue.shift();
          this.headOffset = 0;
        }
      }
      this.onBatch(batch);
    }
  }
}

class AudioSink {
  constructor() {
    this.context = null;
    this.sources = new Set();
    this.nextStartTime = 0;
  }

  ensureContext() {
    if (!this.context) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this.context = new Ctx({ sampleRate: OUTPUT_SAMPLE_RATE });
    }
    if (this.context.state === "suspended") void this.context.resume();
    return this.context;
  }

  play(samples) {
    if (!samples.length) return;
    const ctx = this.ensureContext();
    const buffer = ctx.createBuffer(1, samples.length, OUTPUT_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const startAt = Math.max(this.nextStartTime, ctx.currentTime + 0.02);
    source.start(startAt);
    this.nextStartTime = startAt + buffer.duration;

    this.sources.add(source);
    source.onended = () => this.sources.delete(source);
  }

  flush() {
    this.sources.forEach((source) => {
      try {
        source.stop();
      } catch {
        /* already finished */
      }
    });
    this.sources.clear();
    this.nextStartTime = 0;
  }

  async close() {
    this.flush();
    if (this.context) {
      try {
        await this.context.close();
      } catch {
        /* already closed */
      }
      this.context = null;
    }
  }
}

export function isVoiceSupported() {
  if (typeof window === "undefined") return false;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  return Boolean(
    Ctx &&
      typeof window.AudioWorkletNode !== "undefined" &&
      typeof navigator.mediaDevices?.getUserMedia === "function" &&
      window.WebSocket,
  );
}

/**
 * Open a live voice session. Returns { stop, stream }.
 * Status: "requesting-mic" → "connecting" → "listening"/"thinking"/"speaking" → "stopping" → "idle"
 */
export async function startVoiceSession({
  threadId,
  chatId,
  customerUserId,
  onStatus = () => {},
  onTranscript = () => {},
  onComponents = () => {},
  onPaymentEvent = () => {},
  onTurnSaved = () => {},
  onTurnComplete = () => {},
  onError = () => {},
}) {
  if (!isVoiceSupported()) {
    throw new Error("This browser cannot capture audio.");
  }

  // Audio frames arrive ~20x a second; re-emitting the same status on each one
  // would re-render the whole chat continuously.
  let lastStatus = null;
  const emitStatus = (next) => {
    if (next === lastStatus) return;
    lastStatus = next;
    onStatus(next);
  };

  const sink = new AudioSink();
  // Create playback context inside the initiating click gesture so autoplay
  // policies do not silently block Dockie's first response.
  sink.ensureContext();
  let socket = null;
  let stream = null;
  let captureContext = null;
  let stopped = false;
  let cleanupPromise = null;
  let micReady = false;

  const cleanup = ({ notifyServer = false } = {}) => {
    if (cleanupPromise) return cleanupPromise;
    cleanupPromise = (async () => {
      stopped = true;
      let shouldDrainServerFrames = false;
      try {
        if (notifyServer && socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "stop" }));
          shouldDrainServerFrames = true;
        }
      } catch {
        /* socket already gone */
      }
      stream?.getTracks().forEach((track) => track.stop());
      if (captureContext) {
        try {
          await captureContext.close();
        } catch {
          /* already closed */
        }
      }
      // Give the backend a short window to emit turn_saved/turn_complete and
      // close cleanly before the browser tears down the socket.
      if (shouldDrainServerFrames) {
        await waitForSocketClose(socket, STOP_DRAIN_MS);
      }
      await sink.close();
      try {
        socket?.close();
      } catch {
        /* already closing */
      }
      emitStatus("idle");
    })();
    return cleanupPromise;
  };

  const stop = () => {
    emitStatus("stopping");
    return cleanup({ notifyServer: true });
  };

  emitStatus("requesting-mic");
  try {
    stream = await withTimeout(
      navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      }),
      MIC_TIMEOUT_MS,
      "Microphone permission timed out. Allow mic access in your browser and try again.",
    );
  } catch (err) {
    emitStatus("idle");
    await sink.close();
    throw new Error(
      err?.name === "NotAllowedError"
        ? "Microphone permission was denied."
        : `Could not open the microphone: ${err?.message || err}`,
    );
  }

  try {
    emitStatus("connecting");
    const token = getToken();
    const socketUrl = liveSocketUrl({ baseUrl: getVoiceBaseUrl(), token, threadId });
    socket = new WebSocket(socketUrl);
    socket.binaryType = "arraybuffer";

    let readyReceived = false;
    let readyTimer = null;
    let reportedError = false;

    const fail = (message) => {
      if (stopped || reportedError) return;
      reportedError = true;
      onError(message);
      void cleanup();
    };

    socket.onmessage = (event) => {
      let frame;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }
      switch (frame.type) {
        case "ready":
          readyReceived = true;
          if (readyTimer) clearTimeout(readyTimer);
          micReady = true;
          emitStatus("listening");
          break;
        case "audio":
          emitStatus("speaking");
          sink.play(base64ToFloat32(frame.data));
          break;
        case "transcript":
          if (frame.role === "assistant") {
            emitStatus("speaking");
          } else if (frame.final) {
            emitStatus("thinking");
          } else {
            emitStatus("listening");
          }
          onTranscript({
            role: frame.role,
            text: frame.text,
            final: Boolean(frame.final),
          });
          break;
        case "interrupted":
          sink.flush();
          emitStatus("listening");
          break;
        case "ui":
          onComponents(frame.components || []);
          break;
        case "state":
          if (frame.payment_event) onPaymentEvent(frame.payment_event);
          break;
        case "turn_saved":
          onTurnSaved(frame.messages || []);
          break;
        case "turn_complete":
          onTurnComplete(frame);
          emitStatus("listening");
          break;
        case "error":
          fail(frame.message || "Voice session failed.");
          break;
        default:
          break;
      }
    };

    socket.onerror = () => {
      fail("Lost the connection to the voice service.");
    };
    socket.onclose = (event) => {
      if (readyTimer) clearTimeout(readyTimer);
      // The session only ends on request. Any other close means the backend
      // dropped it, so surface why instead of silently going idle.
      if (!stopped) fail(describeCloseEvent(event, { established: readyReceived }));
    };

    await withTimeout(
      new Promise((resolve, reject) => {
        socket.onopen = resolve;
        socket.addEventListener(
          "error",
          () => reject(new Error("Could not reach the voice service.")),
          { once: true },
        );
      }),
      CONNECT_TIMEOUT_MS,
      "Timed out connecting to the voice service. Check that /v1/agents/live is deployed.",
    );

    socket.send(
      JSON.stringify({
        type: "start",
        threadId,
        chatId: chatId || threadId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        offsetMinutes: -new Date().getTimezoneOffset(),
        customerUserId: customerUserId || undefined,
      }),
    );

    emitStatus("waiting-ready");

    readyTimer = setTimeout(() => {
      if (!readyReceived && !stopped) {
        fail(
          "Voice service did not respond. Ask the backend team to deploy /v1/agents/live (feat/adk-live-voice) to dev-ai.dockie.ai.",
        );
      }
    }, READY_TIMEOUT_MS);

    captureContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: INPUT_SAMPLE_RATE,
    });
    const workletUrl = URL.createObjectURL(
      new Blob([CAPTURE_WORKLET], { type: "text/javascript" }),
    );
    try {
      await withTimeout(
        captureContext.audioWorklet.addModule(workletUrl),
        8_000,
        "Could not start audio capture in this browser.",
      );
    } finally {
      URL.revokeObjectURL(workletUrl);
    }
    if (captureContext.state === "suspended") await captureContext.resume();

    const source = captureContext.createMediaStreamSource(stream);
    const capture = new AudioWorkletNode(captureContext, "pcm-capture");
    const batcher = new PcmBatcher(captureContext.sampleRate, (batch) => {
      if (stopped || !micReady || socket.readyState !== WebSocket.OPEN) return;
      socket.send(
        floatToPcm16(resample(batch, captureContext.sampleRate, INPUT_SAMPLE_RATE)),
      );
    });
    capture.port.onmessage = (event) => batcher.push(event.data);
    source.connect(capture);

    const mute = captureContext.createGain();
    mute.gain.value = 0;
    capture.connect(mute);
    mute.connect(captureContext.destination);

    return { stop, stream };
  } catch (err) {
    await cleanup();
    throw err;
  }
}
